import type { Message } from 'lib-shared-legacy-types';
import type {
  ConversationEmailMessageAddedEvent,
  ConversationMessengerMessageAddedEvent,
} from 'lib-shared-types-api';
import { ComponentProps } from 'react';

import { getMessageMarkup } from 'app/utils/messages';
import { HTMLShadowDomRenderer } from 'components/HTMLShadowDomRenderer';
import { ConversationEventGroupConfigBase } from 'components/conversation/conversationEventGroups/conversationEventGroups.types';

import { ConversationTextualMessage } from '../../textualMessage/conversationTextualMessage';
import { ConversationTextualMessagePropBase } from '../../textualMessage/conversationTextualMessage.types';

/** Represents the conversation email event group configuration. */
export type ConversationHTMLEventGroupConfig = ConversationEventGroupConfigBase<
  'group:contact_form' | 'group:email' | 'group:messenger',
  Pick<ConversationHTMLEventGroupProps, 'hideAttachments' | 'hideGutterActions'>
>;

type ConversationHTMLEventGroupProps = ConversationTextualMessagePropBase<
  ConversationEmailMessageAddedEvent | ConversationMessengerMessageAddedEvent,
  Pick<
    ComponentProps<typeof ConversationTextualMessage.Events>,
    'hideAttachments'
  >
>;

/** Represents the component for displaying a conversation email event group. */
export const ConversationHTMLEventGroup = ({
  author,
  className,
  events,
  hideAttachments,
  hideGutterActions,
}: ConversationHTMLEventGroupProps): JSX.Element => {
  const timestamp = events[events.length - 1].created_at;

  return (
    <ConversationTextualMessage
      className={className}
      author={author}
      timestamp={timestamp}
      hideGutterActions={hideGutterActions}
      variant={
        events[0].direction === 'outbound'
          ? 'textualOutbound'
          : 'textualInbound'
      }
    >
      <ConversationTextualMessage.Events
        events={events}
        hideAttachments={hideAttachments}
        renderEventText={(event) =>
          event.text && (
            <HTMLShadowDomRenderer html={getMessageMarkup(event as Message)} />
          )
        }
      />
    </ConversationTextualMessage>
  );
};
